/**
 * Markdown 渲染器组件
 * 支持完整的 Markdown 语法，包括代码高亮、表格、列表等
 */

import { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface MarkdownRendererProps {
  /** Markdown 源文本 */
  content: string;
  /** 额外的 CSS 类名 */
  className?: string;
}

/**
 * Markdown 渲染器
 *
 * 功能特性：
 * - 支持标准 Markdown 语法
 * - 支持 GitHub Flavored Markdown (GFM)：表格、删除线、任务列表等
 * - 支持代码块语法高亮
 * - 支持内嵌 HTML（向后兼容）
 * - 安全的 XSS 防护
 *
 * @example
 * ```tsx
 * <MarkdownRenderer content="# 标题\n\n这是**粗体**文本" />
 * ```
 */
export const MarkdownRenderer = memo(({ content, className = '' }: MarkdownRendererProps) => {
  if (!content) {
    return null;
  }

  return (
    <div className={`markdown-body ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          // 代码块渲染
          code({ className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            const inline = !className;

            return !inline && language ? (
              <SyntaxHighlighter
                style={oneDark as any}
                language={language}
                PreTag="div"
                className="rounded-lg"
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code
                className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-800 text-sm font-mono"
                {...props}
              >
                {children}
              </code>
            );
          },
          // 图片渲染
          img({ src, alt, ...props }) {
            return (
              <img
                src={src}
                alt={alt}
                className="rounded-lg max-w-full h-auto"
                loading="lazy"
                {...props}
              />
            );
          },
          // 链接渲染
          a({ href, children, ...props }) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
                {...props}
              >
                {children}
              </a>
            );
          },
          // 表格渲染
          table({ children, ...props }) {
            return (
              <div className="overflow-x-auto my-4">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-300" {...props}>
                  {children}
                </table>
              </div>
            );
          },
          // 表头渲染
          thead({ children, ...props }) {
            return (
              <thead className="bg-gray-50" {...props}>
                {children}
              </thead>
            );
          },
          // 表头单元格渲染
          th({ children, ...props }) {
            return (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider" {...props}>
                {children}
              </th>
            );
          },
          // 表格单元格渲染
          td({ children, ...props }) {
            return (
              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 border-t" {...props}>
                {children}
              </td>
            );
          },
          // 引用块渲染
          blockquote({ children, ...props }) {
            return (
              <blockquote className="border-l-4 border-gray-300 pl-4 py-2 my-4 italic text-gray-600" {...props}>
                {children}
              </blockquote>
            );
          },
          // 分隔线渲染
          hr({ ...props }) {
            return <hr className="my-6 border-gray-300" {...props} />;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';
